import type { NgZone } from '@angular/core';
import type { NgSelectComponent } from '@ng-select/ng-select';

/**
 * An ng-select with `appendTo="body"` places its options panel ONCE, when it
 * opens. Moving the page then - scrolling a section, or panning / zooming a
 * canvas (a CSS transform, which fires no scroll event) - left the panel
 * floating over other fields. While open, call this: every frame the panel is
 * kept under its field, and it closes once the field is out of sight (off
 * screen, or clipped by a scrolling / overflow-hidden section). Returns the
 * function that stops it (call on close).
 */
export function followScrollWhileOpen(select: NgSelectComponent, zone: NgZone): () => void {
  const field = select.element;
  const clippers = clippingAncestors(field);
  let last = '';
  let frame = 0;
  const tick = () => {
    frame = 0;
    if (!select.isOpen()) return;
    const rect = field.getBoundingClientRect();
    if (!isVisible(rect, clippers)) {
      zone.run(() => select.close());
      return;
    }
    const key = `${rect.left}|${rect.top}|${rect.width}|${rect.height}`;
    if (key !== last) {
      last = key;
      const panel = document.getElementById(select.dropdownId);
      if (panel?.parentElement) {
        // Horizontal: as ng-select places it (left edge and width of the field).
        const parent = panel.parentElement.getBoundingClientRect();
        panel.style.left = `${rect.left - parent.left}px`;
        panel.style.width = panel.style.minWidth = `${rect.width}px`;
      }
      select.dropdownPanel()?.adjustPosition();
    }
    frame = requestAnimationFrame(tick);
  };
  zone.runOutsideAngular(() => { frame = requestAnimationFrame(tick); });
  return () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };
}

/** The ancestors that can hide part of `element` (overflow other than visible). */
function clippingAncestors(element: HTMLElement): HTMLElement[] {
  const result: HTMLElement[] = [];
  for (let node = element.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (`${style.overflow}${style.overflowX}${style.overflowY}`.replace(/visible/g, '').trim()) result.push(node);
  }
  return result;
}

/** Whether any of `rect` is inside the window and every clipping ancestor. */
function isVisible(rect: DOMRect, clippers: HTMLElement[]): boolean {
  if (!rect.width && !rect.height) return false;
  let top = 0, left = 0, bottom = window.innerHeight, right = window.innerWidth;
  for (const clipper of clippers) {
    const box = clipper.getBoundingClientRect();
    top = Math.max(top, box.top);
    left = Math.max(left, box.left);
    bottom = Math.min(bottom, box.bottom);
    right = Math.min(right, box.right);
  }
  return rect.bottom > top && rect.top < bottom && rect.right > left && rect.left < right;
}
